import { ComponentLifecycleTracker } from './componentLifecycleTracker';
import { ComponentUpdateTracker } from './component-updates/ComponentUpdateTracker';
import { FirestoreMonitor } from "./firestoreObservability/FirestoreMonitor";
import { MicroAppsMonitor } from "./micro-apps/MicroAppsMonitor";

interface MemoryMetrics {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  heapUsagePercentage: number;
  deviceMemory: number;
}

interface ResourceMetrics {
  scripts: number;
  stylesheets: number;
  images: number;
  fonts: number;
  other: number;
}

interface BrowserPerformanceMetrics {
  memory?: MemoryMetrics;
  resources?: ResourceMetrics;
  timing?: any;
  timeOrigin?: number;
}

interface VuexModuleSize {
  moduleName: string;
  size: number;
}

interface VuexStoreMetrics {
  totalSize: number;
  modulesSizes: VuexModuleSize[];
}

interface NavigatorWithDeviceMemory extends Navigator {
  deviceMemory?: number;
}

export class BrowserMetrics {
  private static eventBusMonitor: any = null;
  private static firestoreMonitor: FirestoreMonitor | null = null;
  private static microAppsMonitor: MicroAppsMonitor | null = null;
  private static instance: BrowserMetrics;
  private static componentUpdateTracker: ComponentUpdateTracker;
  private static componentLifecycleTracker: typeof ComponentLifecycleTracker | null = null;

  static setEventBusMonitor(monitor: any) {
    this.eventBusMonitor = monitor;
  }

  static setFirestoreMonitor(monitor: any) {
    this.firestoreMonitor = monitor;
  }

  static setMicroAppsMonitor(monitor: MicroAppsMonitor): void {
    this.microAppsMonitor = monitor;
  }

  static getComponentUpdateTracker(): ComponentUpdateTracker {
    return this.componentUpdateTracker;
  }

  static setComponentUpdateTracker(tracker: ComponentUpdateTracker): void {
    this.componentUpdateTracker = tracker;
  }

  static getEventBusMetrics() {
    return {
      activeListeners: this.eventBusMonitor?.getActiveListenersCount() || 0
    };
  }

  static getFirestoreMetrics() {
    return {
      activeListeners: this.firestoreMonitor?.getActiveListenersCount() || 0
    };
  }

  static getFirestoreMonitor() {
    return this.firestoreMonitor;
  }

  static getEventBusMonitor() {
    return this.eventBusMonitor;
  }

  static getMicroAppsMonitor(): MicroAppsMonitor | null {
    return this.microAppsMonitor;
  }

  /**
   * Get browser performance metrics including memory, resources, and timing
   */
  static getBrowserMetrics(): BrowserPerformanceMetrics {
    if (!this.isMemoryAPIAvailable()) return {}

    try {
      const metrics: BrowserPerformanceMetrics = {
        memory: this.getMemoryMetrics(),
        // resources: this.getResourceMetrics(),
        timing: this.getCoreTiming()
      };
      return metrics;
    } catch (e) {
      return {};
    }
  }

  private static getCoreTiming() {
    try {
      // Key performance metrics
      const navStart = performance.timing.navigationStart;
      const loadTime = performance.timing.loadEventEnd - navStart;
      const domReady = performance.timing.domContentLoadedEventEnd - navStart;
      const ttfb = performance.timing.responseStart - navStart;
      const renderTime = performance.timing.domComplete - performance.timing.domLoading;

      // Modern metrics (core web vitals)
      const paintEntries = performance.getEntriesByType('paint');
      const firstPaint = paintEntries.find(e => e.name === 'first-paint')?.startTime;
      const firstContentfulPaint = paintEntries.find(e => e.name === 'first-contentful-paint')?.startTime;

      return {
        pageLoadComplete: loadTime,
        timeToFirstByte: ttfb,
        domReadyTime: domReady,
        renderingTime: renderTime,
        firstPaint,
        firstContentfulPaint,

        // Network breakdown
        dnsTime: performance.timing.domainLookupEnd - performance.timing.domainLookupStart,
        connectionTime: performance.timing.connectEnd - performance.timing.connectStart,
        requestTime: performance.timing.responseEnd - performance.timing.requestStart,
        downloadTime: performance.timing.responseEnd - performance.timing.responseStart,

        // Processing breakdown
        backendTime: performance.timing.responseEnd - navStart,
        frontendTime: performance.timing.loadEventEnd - performance.timing.responseEnd,
        domProcessingTime: performance.timing.domComplete - performance.timing.domLoading
      };
    } catch (e) {
      console.error('Error getting core timing:', e);
      return {};
    }
  }

  /**
   * Get Vuex store size metrics
   */
  static getVuexMetrics(store?: any) {
    if (!store) {
      store = this.getVueStore();
    }

    if (!store) return { totalSize: 0, modules: {} };

    const modules: Record<string, number> = {};
    let totalSize = 0;

    try {
      // Check if store has the expected structure
      if (store._modules && store._modules.root && store.state) {
        // Get all modules
        const storeModules = store._modules.root._children || {};

        // Calculate size for each module
        Object.keys(storeModules).forEach(moduleName => {
          try {
            const moduleState = store.state[moduleName];
            if (moduleState) {
              const moduleSize = this.calculateObjectSize(moduleState);

              modules[moduleName] = moduleSize;
              totalSize += moduleSize;
            }
          } catch (e) {
            console.error(`Error calculating size for module ${moduleName}:`, e);
          }
        });

        // Also include root state if it exists
        if (store.state && typeof store.state === 'object') {
          const rootStateKeys = Object.keys(store.state).filter(
            key => !Object.keys(storeModules).includes(key)
          );

          if (rootStateKeys.length > 0) {
            const rootState = {};
            rootStateKeys.forEach(key => {
              rootState[key] = store.state[key];
            });

            const rootSize = this.calculateObjectSize(rootState);
            if (rootSize > 0) {
              modules['root'] = rootSize;
              totalSize += rootSize;
            }
          }
        }
      } else {
        // Fallback: treat the entire state as one module
        const stateSize = this.calculateObjectSize(store.state || {});
        modules['state'] = stateSize;
        totalSize = stateSize;
      }
    } catch (e) {
      console.error('Error analyzing Vuex store:', e);
    }

    return {
      totalSize,
      modules
    };
  }

  static getVueStore = (): any => {
    try {
      return (window as any)?.$nuxt?.$store || // Nuxt
             (window as any)?.app?.$store ||   // Vue global
             ((document.querySelector('#app') as any)?.__vue__?.$store); // Vue instance
    } catch (e) {
      console.warn('Error accessing Vue store:', e);
      return null;
    }
  };

  /**
   * Format bytes to human readable string
   */
  static formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  private static isMemoryAPIAvailable(): boolean {
    return !!(performance as any).memory;
  }

  private static calculateObjectSize(obj: any): number {
    const objectList = new WeakSet();

    function sizeOf(value: any): number {
      if (value === null) return 0;

      const type = typeof value;
      if (type === 'boolean') return 4;
      if (type === 'number') return 8;
      if (type === 'string') return value.length * 2;
      if (type === 'object') {
        if (objectList.has(value)) return 0;

        objectList.add(value);

        let size = 0;

        if (Array.isArray(value)) {
          size = 0;
          for (let i = 0; i < value.length; i++) {
            size += sizeOf(value[i]);
          }
        } else {
          const keys = Object.keys(value);
          size = 0;
          for (let i = 0; i < keys.length; i++) {
            size += keys[i].length * 2; // key size
            size += sizeOf(value[keys[i]]); // value size
          }
        }

        return size;
      }

      return 0;
    }

    return sizeOf(obj);
  }

  private static getMemoryMetrics(): MemoryMetrics | undefined {
    if (!this.isMemoryAPIAvailable()) {
      return undefined;
    }

    const memory = (performance as any).memory;
    const usedMB = memory.usedJSHeapSize / (1024 * 1024);  // Current usage
    const limitMB = memory.jsHeapSizeLimit / (1024 * 1024); // Memory limit

    return {
      usedJSHeapSize: Number((memory.usedJSHeapSize / 1048576).toFixed(2)),
      totalJSHeapSize: Number((memory.totalJSHeapSize / 1048576).toFixed(2)),
      jsHeapSizeLimit: Number((memory.jsHeapSizeLimit / 1048576).toFixed(2)),
      heapUsagePercentage: Number(((usedMB / limitMB) * 100).toFixed(1)),
      deviceMemory: (navigator as NavigatorWithDeviceMemory)?.deviceMemory ?? 0
    };
  }

  private static getResourceMetrics(): ResourceMetrics {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    return {
      scripts: resources.filter(r => r.initiatorType === 'script').length,
      stylesheets: resources.filter(r => r.initiatorType === 'css').length,
      images: resources.filter(r => r.initiatorType === 'img').length,
      fonts: resources.filter(r => r.initiatorType === 'font').length,
      other: resources.filter(r => !['script', 'css', 'img', 'font'].includes(r.initiatorType)).length
    };
  }
}

// Example usage:
// const browserMetrics = BrowserMetrics.getBrowserMetrics();
// console.log('Memory Usage:', browserMetrics.memory);
// console.log('Resource Counts:', browserMetrics.resources);
//
// const store = /* your Vuex store instance */;
// const storeMetrics = BrowserMetrics.getVuexMetrics(store);
// console.log('Total Store Size:', BrowserMetrics.formatBytes(storeMetrics.totalSize * 1024));
// console.log('Module Sizes:', storeMetrics.modulesSizes.map(m => ({
//   module: m.moduleName,
//   size: BrowserMetrics.formatBytes(m.size * 1024)
// })));
